import { useEffect, useState } from 'react'
import { Link, getRouteApi } from '@tanstack/react-router'
import { AlertCircle, ArrowLeft, CheckCircle2, ExternalLink, PauseCircle, Rocket, Trash2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Select, Textarea } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs } from '@/components/ui/tabs'
import { ServiceStatusBadge, tierLabel } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import {
  useDeletePackage,
  useManagedService,
  usePauseService,
  usePublishService,
  useSavePackage,
  useUpdateService,
} from '@/features/services/api'
import { useMarketplaceCategories } from '@/features/marketplace/api'
import { ApiError } from '@/lib/api'
import { formatRupiah } from '@/lib/format'
import type { ServicePackage } from '@/lib/types'

const route = getRouteApi('/authenticated/app/jasa/$listingId')

const tiers = ['basic', 'standard', 'premium'] as const

export function ServiceEditorPage() {
  const { listingId } = route.useParams()
  const { data, isLoading } = useManagedService(listingId)
  const [tab, setTab] = useState<'informasi' | 'paket' | 'terbit'>('informasi')

  if (isLoading) return <Skeleton className="h-96 rounded-2xl" />

  if (!data) {
    return (
      <Card>
        <EmptyState
          title="Jasa tidak ditemukan"
          description="Etalase mungkin sudah dihapus."
          action={
            <Link to="/app/jasa">
              <Button variant="outline">Kembali</Button>
            </Link>
          }
        />
      </Card>
    )
  }

  const { service, readiness } = data

  return (
    <div className="space-y-6">
      <Link
        to="/app/jasa"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted transition hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Jasa saya
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <ServiceStatusBadge status={service.status} />
            {readiness.ready ? (
              <Badge tone="success">Siap tayang</Badge>
            ) : (
              <Badge tone="warning">{readiness.reasons.length} hal perlu dilengkapi</Badge>
            )}
          </div>
          <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-ink">{service.title}</h1>
          <p className="mt-1 text-sm text-muted">
            {service.packages.length} paket · {service.order_count} pesanan
          </p>
        </div>

        {service.status === 'published' ? (
          <Link to="/jasa/$slug" params={{ slug: service.slug }}>
            <Button variant="outline">
              <ExternalLink className="size-4" />
              Halaman publik
            </Button>
          </Link>
        ) : null}
      </header>

      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: 'informasi', label: 'Informasi' },
          { value: 'paket', label: 'Paket', count: service.packages.length },
          { value: 'terbit', label: 'Tayang' },
        ]}
      />

      {tab === 'informasi' ? <InfoTab listingId={listingId} service={service} /> : null}
      {tab === 'paket' ? <PackagesTab listingId={listingId} packages={service.packages} /> : null}
      {tab === 'terbit' ? (
        <PublishTab listingId={listingId} status={service.status} readiness={readiness} />
      ) : null}
    </div>
  )
}

function InfoTab({
  listingId,
  service,
}: {
  listingId: string
  service: { title: string; description: string; category: string; thumbnail_url: string; skills: string[] }
}) {
  const update = useUpdateService(listingId)
  const { data: categories } = useMarketplaceCategories()
  const toast = useToast()

  const [values, setValues] = useState({
    title: service.title,
    description: service.description,
    category: service.category,
    thumbnail_url: service.thumbnail_url,
  })

  useEffect(() => {
    setValues({
      title: service.title,
      description: service.description,
      category: service.category,
      thumbnail_url: service.thumbnail_url,
    })
  }, [service])

  return (
    <Card>
      <CardHeader title="Informasi jasa" description="Bagian ini yang dibaca calon pembeli lebih dulu." />
      <CardBody className="space-y-4">
        <Input
          label="Judul"
          value={values.title}
          onChange={(event) => setValues((current) => ({ ...current, title: event.target.value }))}
        />
        <Textarea
          label="Deskripsi"
          rows={7}
          value={values.description}
          onChange={(event) => setValues((current) => ({ ...current, description: event.target.value }))}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Bidang"
            value={values.category}
            onChange={(event) => setValues((current) => ({ ...current, category: event.target.value }))}
          >
            {categories?.map((category) => (
              <option key={category.slug} value={category.slug}>
                {category.name}
              </option>
            ))}
          </Select>
          <Input
            label="URL gambar sampul"
            value={values.thumbnail_url}
            onChange={(event) => setValues((current) => ({ ...current, thumbnail_url: event.target.value }))}
          />
        </div>

        <div className="flex justify-end">
          <Button
            loading={update.isPending}
            onClick={() =>
              update.mutate(
                { ...values, skills: service.skills, thumbnail_url: values.thumbnail_url || undefined },
                {
                  onSuccess: () => toast.success('Perubahan tersimpan'),
                  onError: (error) =>
                    toast.error('Gagal menyimpan', error instanceof ApiError ? error.message : undefined),
                },
              )
            }
          >
            Simpan perubahan
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}

function PackagesTab({ listingId, packages }: { listingId: string; packages: ServicePackage[] }) {
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      {tiers.map((tier) => (
        <PackageCard
          key={tier}
          listingId={listingId}
          tier={tier}
          existing={packages.find((pkg) => pkg.tier === tier)}
        />
      ))}
    </div>
  )
}

function PackageCard({
  listingId,
  tier,
  existing,
}: {
  listingId: string
  tier: string
  existing?: ServicePackage
}) {
  const savePackage = useSavePackage(listingId)
  const deletePackage = useDeletePackage(listingId)
  const toast = useToast()

  const [values, setValues] = useState({
    title: '',
    description: '',
    price: 0,
    delivery_days: 7,
    revisions: 1,
    features: '',
  })

  useEffect(() => {
    if (!existing) return
    setValues({
      title: existing.title,
      description: existing.description,
      price: existing.price,
      delivery_days: existing.delivery_days,
      revisions: existing.revisions,
      features: existing.features.join('\n'),
    })
  }, [existing])

  return (
    <Card className={existing ? 'border-brand/30' : ''}>
      <CardHeader
        title={tierLabel[tier] ?? tier}
        description={existing ? formatRupiah(existing.price) : 'Belum disiapkan'}
        action={
          existing ? (
            <Button
              size="sm"
              variant="ghost"
              className="text-danger"
              onClick={() => {
                if (!window.confirm(`Hapus paket ${tierLabel[tier]}?`)) return
                deletePackage.mutate(tier, { onSuccess: () => toast.info('Paket dihapus') })
              }}
            >
              <Trash2 className="size-4" />
            </Button>
          ) : null
        }
      />
      <CardBody className="space-y-3">
        <Input
          label="Nama paket"
          value={values.title}
          onChange={(event) => setValues((current) => ({ ...current, title: event.target.value }))}
          placeholder="Misalnya: Dasbor ringkas"
        />
        <Textarea
          label="Keterangan"
          rows={2}
          value={values.description}
          onChange={(event) => setValues((current) => ({ ...current, description: event.target.value }))}
        />
        <Input
          label="Harga"
          type="number"
          min={0}
          step={100000}
          prefix="Rp"
          value={values.price || ''}
          onChange={(event) => setValues((current) => ({ ...current, price: Number(event.target.value) }))}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Hari kerja"
            type="number"
            min={1}
            value={values.delivery_days}
            onChange={(event) => setValues((current) => ({ ...current, delivery_days: Number(event.target.value) }))}
          />
          <Input
            label="Revisi"
            type="number"
            min={0}
            value={values.revisions}
            onChange={(event) => setValues((current) => ({ ...current, revisions: Number(event.target.value) }))}
          />
        </div>
        <Textarea
          label="Yang termasuk"
          rows={4}
          value={values.features}
          onChange={(event) => setValues((current) => ({ ...current, features: event.target.value }))}
          hint="Satu baris satu butir."
        />

        <Button
          fullWidth
          variant={existing ? 'outline' : 'primary'}
          loading={savePackage.isPending}
          disabled={values.title.trim().length < 3 || values.price <= 0}
          onClick={() =>
            savePackage.mutate(
              {
                tier,
                title: values.title,
                description: values.description,
                price: values.price,
                delivery_days: values.delivery_days,
                revisions: values.revisions,
                features: values.features
                  .split('\n')
                  .map((line) => line.trim())
                  .filter(Boolean)
                  .slice(0, 10),
              },
              {
                onSuccess: () => toast.success(`Paket ${tierLabel[tier]} tersimpan`),
                onError: (error) =>
                  toast.error('Gagal menyimpan paket', error instanceof ApiError ? error.message : undefined),
              },
            )
          }
        >
          {existing ? 'Perbarui paket' : 'Simpan paket'}
        </Button>
      </CardBody>
    </Card>
  )
}

function PublishTab({
  listingId,
  status,
  readiness,
}: {
  listingId: string
  status: string
  readiness: { ready: boolean; reasons: string[] }
}) {
  const publish = usePublishService(listingId)
  const pause = usePauseService(listingId)
  const toast = useToast()

  return (
    <Card>
      <CardHeader title="Kesiapan tayang" description="Diperiksa ulang di server sebelum jasa benar-benar tayang." />
      <CardBody>
        {readiness.ready ? (
          <div className="flex items-start gap-3 rounded-xl bg-success-soft p-4">
            <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-success" />
            <div>
              <p className="font-bold text-ink">Semua syarat terpenuhi</p>
              <p className="mt-0.5 text-sm text-muted">Jasa siap tayang dan langsung dapat dipesan.</p>
            </div>
          </div>
        ) : (
          <ul className="space-y-2.5">
            {readiness.reasons.map((reason) => (
              <li key={reason} className="flex items-start gap-3 rounded-xl bg-warning-soft p-3.5">
                <AlertCircle className="mt-0.5 size-4.5 shrink-0 text-warning" />
                <span className="text-sm text-ink">{reason}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <Button
            size="lg"
            disabled={!readiness.ready || status === 'published'}
            loading={publish.isPending}
            onClick={() =>
              publish.mutate(undefined, {
                onSuccess: () => toast.success('Jasa tayang', 'Etalase Anda sudah dapat dipesan.'),
                onError: (error) =>
                  toast.error('Gagal menayangkan', error instanceof ApiError ? error.message : undefined),
              })
            }
          >
            <Rocket className="size-4.5" />
            {status === 'published' ? 'Sudah tayang' : 'Tayangkan jasa'}
          </Button>

          {status === 'published' ? (
            <Button
              size="lg"
              variant="outline"
              loading={pause.isPending}
              onClick={() =>
                pause.mutate(undefined, {
                  onSuccess: () => toast.info('Jasa dijeda', 'Pesanan baru tidak akan masuk sampai ditayangkan lagi.'),
                })
              }
            >
              <PauseCircle className="size-4.5" />
              Jeda sementara
            </Button>
          ) : null}
        </div>
      </CardBody>
    </Card>
  )
}
